<template>
  <v-container grid-list-xl fluid>
    <v-layout row wrap pa-3>
      <!-- Top Toolbar -->
      <v-flex xs12 class="d-flex align-center py-2">
        <v-btn icon @click="goBack" class="mr-2">
          <v-icon>fa-arrow-left</v-icon>
        </v-btn>
        <span class="headline font-weight-bold">
          Nhập kho xe hàng: {{ currentItem.VehicleCode || ('#' + vehicleId) }}
        </span>
        <v-spacer></v-spacer>
        <v-btn
          color="success"
          dark
          :disabled="loading || currentItem.NumberOfPack === currentItem.ReceivedNumberOfPack"
          @click="showConfirmAllDialog = true"
        >
          <v-icon left>fa-check-double</v-icon>
          Xác nhận nhập cả xe
        </v-btn>
      </v-flex>

      <!-- Summary Cards -->
      <v-flex xs12 class="py-2">
        <v-layout row wrap>
          <v-flex xs12 sm4 md2 v-for="card in summaryCards" :key="card.label" class="py-2">
            <v-card outlined class="summary-card" :class="card.class">
              <div class="summary-card__label">{{ card.label }}</div>
              <div class="summary-card__value">{{ card.value }}</div>
            </v-card>
          </v-flex>
        </v-layout>
      </v-flex>

      <!-- Barcode Scan Area -->
      <v-flex xs12 class="py-2">
        <v-card outlined class="pa-4">
          <v-layout row wrap align-center>
            <v-flex xs12 md6 py-0>
              <v-text-field
                label="Mã vận đơn (Scan hoặc nhập tay)"
                ref="trancode"
                v-model="transactionCode"
                outlined
                dense
                hide-details
                @input="onBarcodeScan"
                @keyup.enter="onManualSubmit"
                :disabled="loading"
                placeholder="Ví dụ: VN123456789"
                prepend-inner-icon="fa-barcode"
              ></v-text-field>
            </v-flex>
            <v-flex xs6 md3 py-0>
              <v-checkbox
                label="Sử dụng máy đọc barcode"
                v-model="useBarcodeReader"
                hide-details
              ></v-checkbox>
            </v-flex>
            <v-flex xs6 md3 py-0>
              <v-checkbox
                label="Tự động xác nhận"
                v-model="autoConfirm"
                hide-details
              ></v-checkbox>
            </v-flex>
          </v-layout>
        </v-card>
      </v-flex>

      <!-- Packages Table -->
      <v-flex xs12 class="mt-4 pa-0">
        <v-card outlined class="pa-0">
          <v-card-title class="px-4 py-3 d-flex align-center">
            <span class="subtitle-1 font-weight-bold">Danh sách mã vận đơn của xe</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="searchText"
              append-icon="fa-search"
              label="Tìm nhanh trên danh sách"
              single-line
              hide-details
              dense
              class="max-width-300"
            ></v-text-field>
          </v-card-title>
          <v-card-text class="pa-0">
            <div v-if="!groupedTransportSources.length" class="empty-state">
              Chưa có đơn hàng hoặc bao hàng nào được xếp lên xe này.
            </div>
            <div v-else class="table-wrap">
              <table class="order-table">
                <thead>
                  <tr>
                    <th>Mã đơn/bao</th>
                    <th>Mã vận đơn</th>
                    <th class="text-right">Cân nặng</th>
                    <th class="text-right">Số kiện</th>
                    <th class="text-right">Chiều dài</th>
                    <th class="text-right">Chiều rộng</th>
                    <th class="text-right">Chiều cao</th>
                    <th class="text-right">Thể tích</th>
                    <th>Trạng thái</th>
                    <th>Ghi chú</th>
                    <th class="text-center">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="group in filteredGroupedSources">
                    <tr
                      v-for="(pkg, index) in group.packages"
                      :key="`${group.type}-${group.id}-${pkg.ID || index}`"
                      :class="{ 'highlighted-row': pkg.TransactionCode === highlightedCode }"
                    >
                      <!-- Group cell (rowspan) -->
                      <td v-if="index === 0" :rowspan="group.packages.length" class="order-table__group">
                        <div class="font-weight-bold">{{ group.code }}</div>
                        <div class="caption grey--text text--darken-1">{{ group.subtitle }}</div>
                        <div class="caption grey--text text--darken-1">
                          {{ group.packages.length }} kiện · {{ formatNumber(group.totalWeight) }} kg · {{ formatNumber(group.totalVolume) }} m³
                        </div>
                      </td>

                      <!-- Package Info -->
                      <td :class="pkg.OrderID == null ? 'text-red font-weight-bold' : ''">
                        {{ pkg.TransactionCode || '--' }}
                      </td>

                      <!-- Editable Weight -->
                      <td class="text-right">
                        <v-edit-dialog
                          :return-value.sync="pkg.Weight"
                          large
                          lazy
                          persistent
                          @save="saveTranWeight(pkg)"
                          v-if="pkg.Status <= 4"
                        >
                          <div class="editable-cell">{{ pkg.Weight }} kg</div>
                          <template v-slot:input>
                            <v-text-field
                              v-model="pkg.Weight"
                              type="number"
                              label="Cân nặng"
                              step=".1"
                              single-line
                              autofocus
                            ></v-text-field>
                          </template>
                        </v-edit-dialog>
                        <span v-else>{{ pkg.Weight }} kg</span>
                      </td>

                      <!-- Editable NumberOfPack -->
                      <td class="text-right">
                        <v-edit-dialog
                          :return-value.sync="pkg.NumberOfPack"
                          large
                          lazy
                          persistent
                          @save="saveTranWeight(pkg)"
                          v-if="pkg.Status <= 4"
                        >
                          <div class="editable-cell">{{ pkg.NumberOfPack || 0 }}</div>
                          <template v-slot:input>
                            <v-text-field
                              v-model="pkg.NumberOfPack"
                              type="number"
                              label="Số kiện"
                              single-line
                              autofocus
                            ></v-text-field>
                          </template>
                        </v-edit-dialog>
                        <span v-else>{{ pkg.NumberOfPack || 0 }}</span>
                      </td>

                      <!-- Editable Length -->
                      <td class="text-right">
                        <v-edit-dialog
                          :return-value.sync="pkg.Length"
                          large
                          lazy
                          persistent
                          @save="saveTranWeight(pkg)"
                          v-if="pkg.Status <= 4"
                        >
                          <div class="editable-cell">{{ pkg.Length | currency('', 1) }} cm</div>
                          <template v-slot:input>
                            <h-currency-input
                              label="Chiều dài (cm)"
                              :decimal="1"
                              v-model="pkg.Length"
                              single-line
                              autofocus
                            ></h-currency-input>
                          </template>
                        </v-edit-dialog>
                        <span v-else>{{ pkg.Length | currency('', 1) }} cm</span>
                      </td>

                      <!-- Editable Width -->
                      <td class="text-right">
                        <v-edit-dialog
                          :return-value.sync="pkg.Width"
                          large
                          lazy
                          persistent
                          @save="saveTranWeight(pkg)"
                          v-if="pkg.Status <= 4"
                        >
                          <div class="editable-cell">{{ pkg.Width | currency('', 1) }} cm</div>
                          <template v-slot:input>
                            <h-currency-input
                              label="Chiều rộng (cm)"
                              :decimal="1"
                              v-model="pkg.Width"
                              single-line
                              autofocus
                            ></h-currency-input>
                          </template>
                        </v-edit-dialog>
                        <span v-else>{{ pkg.Width | currency('', 1) }} cm</span>
                      </td>

                      <!-- Editable Height -->
                      <td class="text-right">
                        <v-edit-dialog
                          :return-value.sync="pkg.Height"
                          large
                          lazy
                          persistent
                          @save="saveTranWeight(pkg)"
                          v-if="pkg.Status <= 4"
                        >
                          <div class="editable-cell">{{ pkg.Height | currency('', 1) }} cm</div>
                          <template v-slot:input>
                            <h-currency-input
                              label="Chiều cao (cm)"
                              :decimal="1"
                              v-model="pkg.Height"
                              single-line
                              autofocus
                            ></h-currency-input>
                          </template>
                        </v-edit-dialog>
                        <span v-else>{{ pkg.Height | currency('', 1) }} cm</span>
                      </td>

                      <!-- Editable Volume -->
                      <td class="text-right">
                        <v-edit-dialog
                          :return-value.sync="pkg.Volume"
                          large
                          lazy
                          persistent
                          @save="saveTranWeight(pkg)"
                          v-if="pkg.Status <= 4"
                        >
                          <div class="editable-cell">{{ pkg.Volume | currency('', 2) }} m³</div>
                          <template v-slot:input>
                            <h-currency-input
                              label="Thể tích"
                              :decimal="2"
                              v-model="pkg.Volume"
                              single-line
                              autofocus
                            ></h-currency-input>
                          </template>
                        </v-edit-dialog>
                        <span v-else>{{ pkg.Volume | currency('', 2) }} m³</span>
                      </td>

                      <!-- Status -->
                      <td>
                        <v-chip small :color="getPackageStatusColor(pkg.Status)" text-color="white">
                          {{ getPackageStatusName(pkg.Status) }}
                        </v-chip>
                      </td>

                      <!-- Editable Note -->
                      <td>
                        <v-edit-dialog
                          :return-value.sync="pkg.StoreVNNote"
                          large
                          lazy
                          persistent
                          @save="saveNoteItem(pkg)"
                        >
                          <div class="note-cell truncate" v-bind:title="pkg.StoreVNNote">
                            {{ pkg.StoreVNNote || '--' }}
                          </div>
                          <template v-slot:input>
                            <v-textarea
                              v-model="pkg.StoreVNNote"
                              label="Ghi chú"
                              autofocus
                              hide-details
                            ></v-textarea>
                          </template>
                        </v-edit-dialog>
                      </td>

                      <!-- Actions -->
                      <td class="text-center nowrap">
                        <v-tooltip top v-if="pkg.Status <= 4">
                          <template v-slot:activator="{ on }">
                            <v-btn
                              v-on="on"
                              icon
                              small
                              color="success"
                              class="mr-1"
                              @click="confirmSinglePackage(pkg)"
                            >
                              <v-icon small>fa-check</v-icon>
                            </v-btn>
                          </template>
                          <span>Xác nhận nhập kho VN</span>
                        </v-tooltip>
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-btn
                              v-on="on"
                              icon
                              small
                              color="red darken-2"
                              @click="printTransaction(pkg.ID, pkg.PackageType)"
                            >
                              <v-icon small>fa-print</v-icon>
                            </v-btn>
                          </template>
                          <span>In tem</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Overlay Loader -->
    <v-overlay :value="loading" z-index="999999">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>

    <!-- Dialog confirm all -->
    <v-dialog v-model="showConfirmAllDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline py-4">Xác nhận nhập kho toàn bộ xe</v-card-title>
        <v-card-text class="pt-2">
          Bạn có chắc chắn muốn xác nhận nhập kho VN cho toàn bộ các mã vận đơn còn lại trên xe <strong>{{ currentItem.VehicleCode }}</strong>?
          <br />
          Hành động này sẽ thay đổi trạng thái của toàn bộ kiện trên xe thành <strong>Đã về kho VN</strong>.
        </v-card-text>
        <v-card-actions class="pb-4 px-4">
          <v-spacer></v-spacer>
          <v-btn color="grey lighten-2" @click="showConfirmAllDialog = false">Hủy</v-btn>
          <v-btn color="success" class="white--text" @click="confirmAllVehicle">Đồng ý</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import configs from "../../configs";
import _ from "lodash";
import { print_transaction_v2, request_detail } from "@/commons/hpod";
import Vue from 'vue';
import { sendSuccessNotice, sendErrorNotice } from "@/commons/utils";

export default {
  data() {
    return {
      vehicleId: null,
      transactionCode: "",
      searchText: "",
      useBarcodeReader: true,
      autoConfirm: true,
      highlightedCode: "",
      showConfirmAllDialog: false,
      packageStatuses: configs.PACKAGE_STATUS,
    };
  },
  computed: {
    ...mapState({
      currentItem: state => state.vehicle.selected,
      loading: state => state.vehicle.loading,
    }),
    summaryCards() {
      const smallPackages = this.currentItem.SmallPackages || [];
      const receivedCount = _.sumBy(_.filter(smallPackages, pkg => pkg.Status >= 4), pkg => Number(pkg.NumberOfPack) || 0);
      return [
        { label: "Số đơn", value: (this.currentItem.OrderIDs || []).length },
        { label: "Số bao", value: (this.currentItem.BigPackageIDs || []).length },
        { label: "Số kiện", value: _.sumBy(smallPackages, pkg => Number(pkg.NumberOfPack) || 0) },
        {
          label: "Số kiện đã nhận",
          value: receivedCount,
          class: "received-card font-weight-bold"
        },
        { label: "Tổng cân", value: `${this.formatNumber(this.currentItem.TotalWeight)} kg` },
        { label: "Tổng khối", value: `${this.formatNumber(this.currentItem.TotalVolume)} m³` }
      ];
    },
    groupedTransportSources() {
      const selectedBigPackageIds = new Set((this.currentItem.BigPackageIDs || []).map(id => Number(id)));
      const ordersById = _.keyBy(this.currentItem.Orders || [], "ID");
      const bigPackagesById = _.keyBy(this.currentItem.BigPackages || [], "ID");
      
      const byBigPackage = _.chain(this.currentItem.SmallPackages || [])
        .filter(item => selectedBigPackageIds.has(Number(item.BigPackageID)))
        .groupBy(item => item.BigPackageID)
        .map((packages, bigPackageId) => {
          const item = bigPackagesById[bigPackageId] || {};
          return {
            type: "bigpackage",
            id: Number(bigPackageId),
            code: item.PackageCode || packages[0].PackageCode || `#${bigPackageId}`,
            subtitle: "Bao hàng",
            packages,
            totalWeight: _.sumBy(packages, pkg => Number(pkg.Weight) || 0),
            totalVolume: _.sumBy(packages, pkg => Number(pkg.Volume) || 0)
          };
        })
        .orderBy(["id"], ["desc"])
        .value();

      const byOrder = _.chain(this.currentItem.SmallPackages || [])
        .filter(item => !selectedBigPackageIds.has(Number(item.BigPackageID)))
        .groupBy(item => item.OrderID)
        .map((packages, orderId) => {
          const item = ordersById[orderId] || {};
          return {
            type: "order",
            id: Number(orderId),
            code: item.OrderCode || packages[0].OrderCode || `MD${orderId}`,
            subtitle: item.Username || "Đơn hàng",
            packages,
            totalWeight: _.sumBy(packages, pkg => Number(pkg.Weight) || 0),
            totalVolume: _.sumBy(packages, pkg => Number(pkg.Volume) || 0)
          };
        })
        .orderBy(["id"], ["desc"])
        .value();

      return [...byBigPackage, ...byOrder];
    },
    filteredGroupedSources() {
      if (!this.searchText || !this.searchText.trim()) {
        return this.groupedTransportSources;
      }
      const q = this.searchText.trim().toLowerCase();
      return _.chain(this.groupedTransportSources)
        .map(group => {
          const matchedPkgs = _.filter(group.packages, pkg => {
            return (
              (pkg.TransactionCode && pkg.TransactionCode.toLowerCase().includes(q)) ||
              (group.code && group.code.toLowerCase().includes(q)) ||
              (pkg.StoreVNNote && pkg.StoreVNNote.toLowerCase().includes(q))
            );
          });
          if (matchedPkgs.length > 0) {
            return {
              ...group,
              packages: matchedPkgs
            };
          }
          return null;
        })
        .filter(Boolean)
        .value();
    }
  },
  mounted() {
    this.vehicleId = Number(this.$route.params.Pid);
    this.refreshData();
    this.$nextTick(() => {
      this.focusBarcodeField();
    });
  },
  methods: {
    goBack() {
      this.$router.push({ name: "StoreManagerVN" });
    },
    refreshData() {
      if (this.vehicleId) {
        var param = {
          pagination: {
            ...configs.PAGINATION,
            itemsPerPage: 100000
          }
        };
        this.$store.dispatch("vehicle/getDetail", { id: this.vehicleId, param });
      }
    },
    focusBarcodeField() {
      if (this.$refs.trancode) {
        this.$refs.trancode.focus();
      }
    },
    onBarcodeScan() {
      if (this.useBarcodeReader && this.transactionCode) {
        this.processBarcode(this.transactionCode.trim());
      }
    },
    onManualSubmit() {
      if (!this.useBarcodeReader && this.transactionCode) {
        this.processBarcode(this.transactionCode.trim());
      }
    },
    processBarcode(code) {
      if (!code) return;
      
      const smallPackages = this.currentItem.SmallPackages || [];
      const foundPkg = _.find(smallPackages, pkg => {
        return pkg.TransactionCode && pkg.TransactionCode.toLowerCase() === code.toLowerCase();
      });

      if (!foundPkg) {
        sendErrorNotice(`Mã vận đơn ${code} không có trên xe này!`);
        this.playAudioFeedback(false);
        this.transactionCode = "";
        return;
      }

      if (foundPkg.Status >= 4) {
        sendErrorNotice(`Mã vận đơn ${code} đã được nhập kho trước đó!`);
        this.playAudioFeedback(false);
        this.highlightedCode = foundPkg.TransactionCode;
        this.transactionCode = "";
        return;
      }

      this.highlightedCode = foundPkg.TransactionCode;
      
      if (this.autoConfirm) {
        this.confirmSinglePackage(foundPkg);
      } else {
        sendSuccessNotice(`Đã tìm thấy mã vận đơn ${code} trên xe!`);
        this.playAudioFeedback(true);
        this.transactionCode = "";
      }
    },
    confirmSinglePackage(pkg) {
      const data = {
        ID: pkg.ID,
        Status: 4, // Đã về kho VN
        TransactionView: 'store_vn',
        BigPackageId: pkg.BigPackageID,
        OrderID: pkg.OrderID,
        PackageType: pkg.PackageType,
        Weight: pkg.Weight,
        Length: pkg.Length,
        Width: pkg.Width,
        Height: pkg.Height,
        Volume: pkg.Volume,
        NumberOfPack: pkg.NumberOfPack || 1
      };
      
      this.$store.dispatch("orderTransaction/setDetail", {
        data: data,
        id: pkg.ID
      }).then(() => {
        this.playAudioFeedback(true);
        this.refreshData();
        this.transactionCode = "";
        this.focusBarcodeField();
      }).catch(() => {
        sendErrorNotice(`Không thể xác nhận nhập kho cho mã ${pkg.TransactionCode}`);
        this.playAudioFeedback(false);
      });
    },
    confirmAllVehicle() {
      this.showConfirmAllDialog = false;
      this.$store.dispatch("vehicle/confirmStoreVn", this.vehicleId).then(() => {
        this.refreshData();
      });
    },
    saveTranWeight(item) {
      const data = {
        ID: item.ID,
        OrderID: item.OrderID,
        PackageType: item.PackageType,
        NumberOfPack: item.NumberOfPack,
        Weight: item.Weight,
        Length: item.Length,
        Width: item.Width,
        Height: item.Height,
        Volume: item.Volume,
        BigPackageId: item.BigPackageID,
        TransactionView: 'store_vn'
      };
      data.ModifiedBy = this.$store.state.authentication.user.username;
      this.$store.dispatch("orderTransaction/setDetail", {
        data: data,
        id: item.ID
      }).then(() => {
        this.refreshData();
      });
    },
    saveNoteItem(item) {
      this.$store.dispatch("orderTransaction/setDetail", {
        data: { StoreVNNote: item.StoreVNNote, TransactionView: 'store_vn', BigPackageId: item.BigPackageID },
        id: item.ID
      });
    },
    printTransaction(id, package_type) {
      request_detail('order_transactions/print', ret => {
        let item = ret.data;
        print_transaction_v2(item);
      }, Vue.http, id, this.$store.state.authentication.token, { 'PackageType': package_type });
    },
    getPackageStatusColor(status) {
      const match = _.find(this.packageStatuses, { value: status });
      return match ? match.color : "grey";
    },
    getPackageStatusName(status) {
      const match = _.find(this.packageStatuses, { value: status });
      return match ? match.name : "Không xác định";
    },
    formatNumber(value) {
      return (Number(value) || 0).toLocaleString("vi-VN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    },
    playAudioFeedback(isSuccess) {
      try {
        const context = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = context.createOscillator();
        const gainNode = context.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(context.destination);
        
        if (isSuccess) {
          oscillator.frequency.setValueAtTime(600, context.currentTime);
          oscillator.frequency.exponentialRampToValueAtTime(800, context.currentTime + 0.1);
          gainNode.gain.setValueAtTime(0.1, context.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.15);
          oscillator.start(context.currentTime);
          oscillator.stop(context.currentTime + 0.15);
        } else {
          oscillator.type = "sawtooth";
          oscillator.frequency.setValueAtTime(150, context.currentTime);
          gainNode.gain.setValueAtTime(0.2, context.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.3);
          oscillator.start(context.currentTime);
          oscillator.stop(context.currentTime + 0.3);
        }
      } catch (e) {
        // AudioContext not supported or blocked
      }
    }
  }
};
</script>

<style scoped>
.max-width-300 {
  max-width: 300px;
}
.summary-card {
  padding: 16px;
  min-height: 80px;
  border-radius: 12px;
  background: linear-gradient(135deg, #f7fafc 0%, #eef3ff 100%);
  border-color: #e3e6eb;
}

.summary-card__label {
  font-size: 11px;
  color: #5f6b7a;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.summary-card__value {
  font-size: 20px;
  font-weight: 700;
  color: #1f2d3d;
}

.received-card {
  background: linear-gradient(135deg, #f0fff4 0%, #dcffe4 100%);
  border-color: #c6f6d5;
}
.received-card .summary-card__label {
  color: #276749;
}
.received-card .summary-card__value {
  color: #22543d;
}

.empty-state {
  padding: 40px 16px;
  text-align: center;
  color: #7a8796;
}

.table-wrap {
  overflow-x: auto;
}

.order-table {
  width: 100%;
  border-collapse: collapse;
}

.order-table th,
.order-table td {
  padding: 10px 12px;
  border-top: 1px solid #e6ebf2;
  vertical-align: middle;
  font-size: 13px;
}

.order-table th {
  background: #f8fafc;
  color: #445165;
  font-weight: 700;
  text-align: left;
  white-space: nowrap;
}

.order-table__group {
  min-width: 180px;
  background: #fcfdff;
  border-right: 1px solid #e6ebf2;
}

.editable-cell {
  border-bottom: 1px dotted #1976d2;
  cursor: pointer;
  display: inline-block;
}

.note-cell {
  max-width: 150px;
  cursor: pointer;
  border-bottom: 1px dotted #708090;
}

.highlighted-row {
  background-color: #fff9db !important;
  animation: flash 1s ease-in-out;
}

@keyframes flash {
  0% { background-color: #fff3bf; }
  100% { background-color: #fff9db; }
}

.nowrap {
  white-space: nowrap;
}
</style>
